import { useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { z } from 'zod';
import { passwordSchema } from '@lightbm/shared';
import { Button } from '@/components/ui/Button';
import { Field } from '@/components/ui/Field';
import { Notice } from '@/components/ui/Notice';
import { useErrorText } from '@/i18n/messages';
import { authApi } from '../api';
import { PasswordInput } from '../components/PasswordInput';

const newPasswordForm = z
  .object({ password: passwordSchema, confirm: z.string() })
  .refine((data) => data.password === data.confirm, {
    path: ['confirm'],
    message: 'validation.passwordsDiffer',
  });
type NewPasswordForm = z.infer<typeof newPasswordForm>;

/** Completes the "forgot password" flow from the emailed link. */
export function ResetPasswordPage() {
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const errorText = useErrorText();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const [formError, setFormError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<NewPasswordForm>({
    resolver: zodResolver(newPasswordForm),
    defaultValues: { password: '', confirm: '' },
  });

  const onSubmit = handleSubmit(async (values) => {
    setFormError(null);
    try {
      await authApi.resetPassword({ token, password: values.password });
      void navigate('/sign-in?reason=password-reset', { replace: true });
    } catch (error) {
      setFormError(errorText(error));
    }
  });

  return (
    <>
      <h1 className="text-[30px] leading-tight font-semibold tracking-tight">{t('reset.title')}</h1>
      <p className="mt-2 text-[17px] text-ink-2">{t('reset.subtitle')}</p>

      <div className="mt-8 rounded-[16px] border border-line bg-surface p-6 shadow-card sm:p-8">
        {token.length < 20 ? (
          <Notice tone="danger" title={t('reset.invalidTitle')}>
            <Link to="/forgot-password" className="font-medium text-primary underline">
              {t('reset.requestAgain')}
            </Link>
          </Notice>
        ) : (
          <form onSubmit={onSubmit} noValidate className="space-y-5">
            <Field
              label={t('fields.newPassword')}
              error={errors.password}
              hint={t('password.rule')}
            >
              <PasswordInput {...register('password')} autoComplete="new-password" />
            </Field>
            <Field label={t('fields.confirmPassword')} error={errors.confirm}>
              <PasswordInput {...register('confirm')} autoComplete="new-password" />
            </Field>
            {formError && (
              <Notice tone="danger" role="alert">
                {formError}{' '}
                <Link to="/forgot-password" className="font-medium underline">
                  {t('reset.requestAgain')}
                </Link>
              </Notice>
            )}
            <Button type="submit" size="lg" loading={isSubmitting} className="w-full">
              {t('reset.submit')}
            </Button>
          </form>
        )}
      </div>
    </>
  );
}
